import { Box } from "@mui/material";
import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import Flex from "@/components/Flex";
import { NAVBAR_HEIGHT } from "@/components/app/nav-bar/NavBar";
import { CHART_COLORS } from "@/components/app/provider/patient/chart";
import useSelectedPatient, {
  PatientProfile,
} from "@/hooks/local-storage/use-selected-patient";
import useFcmSmsCountGet from "@/queries/fcm/use-fcm-sms-count-get";
import { useGetPatient } from "@/queries/use-get-patient-uuid";
import useFcmSmsEventListener from "@/services/fcm/listeners/FcmSmsEventListener";
import PatientChartTabs from "./PatientChartTabs";
import PatientSideBar from "./PatientSideBar";
import {
  VisitNotePanelProvider,
  useVisitNotePanel,
} from "./context/VisitNotePanelContext";

/* ─── Pinboard Bar — shows current patient as active tab ─── */
// const PinboardBar = ({
//   patientName,
//   onClose,
// }: {
//   patientName: string;
//   onClose: () => void;
// }) => (
//   <Box
//     display="flex"
//     alignItems="flex-end"
//     justifyContent="space-between"
//     height="32px"
//     px="24px"
//     flexShrink={0}
//     sx={{
//       bgcolor: "#E5F3FF",
//       borderBottom: `1px solid ${CHART_COLORS.primary}`,
//     }}
//   >
//     <Flex alignItems="flex-end" gap="4px">
//       <Box
//         display="flex"
//         alignItems="center"
//         gap="4px"
//         px="8px"
//         py="4px"
//         sx={{
//           borderTopLeftRadius: "4px",
//           borderTopRightRadius: "4px",
//           bgcolor: CHART_COLORS.bgWarning,
//           borderTop: `1px solid ${CHART_COLORS.primary}`,
//           borderLeft: `1px solid ${CHART_COLORS.primary}`,
//           borderRight: `1px solid ${CHART_COLORS.primary}`,
//           mb: "-1px",
//           pb: "5px",
//           cursor: "default",
//         }}
//       >
//         <PushPinOutlinedIcon
//           sx={{
//             fontSize: "16px",
//             color: CHART_COLORS.textDark,
//             transform: "rotate(45deg)",
//           }}
//         />
//         <Typography
//           sx={{
//             ...CHART_TYPOGRAPHY.body5,
//             fontFamily: CHART_TYPOGRAPHY.fontFamily,
//             fontWeight: CHART_TYPOGRAPHY.weight.regular,
//             color: CHART_COLORS.textDark,
//             whiteSpace: "nowrap",
//           }}
//         >
//           {patientName}
//         </Typography>
//         <CloseIcon
//           onClick={onClose}
//           sx={{
//             fontSize: "16px",
//             color: CHART_COLORS.textDark,
//             ml: "2px",
//             cursor: "pointer",
//             "&:hover": { opacity: 0.7 },
//           }}
//         />
//       </Box>
//     </Flex>
//     <Flex
//       alignItems="center"
//       height="100%"
//       gap="4px"
//       sx={{ cursor: "pointer" }}
//     >
//       <PushPinOutlinedIcon
//         sx={{
//           fontSize: "16px",
//           color: CHART_COLORS.primary,
//           transform: "rotate(45deg)",
//         }}
//       />
//       <Typography
//         sx={{
//           ...CHART_TYPOGRAPHY.body5,
//           fontFamily: CHART_TYPOGRAPHY.fontFamily,
//           color: CHART_COLORS.primary,
//           whiteSpace: "nowrap",
//         }}
//       >
//         Unpin All
//       </Typography>
//     </Flex>
//   </Box>
// );

// const PATIENT_LIST_PATH = "/app/provider/patients";

const PatientChartNewLayoutInner = () => {
  // const navigate = useNavigate();
  const { patientId } = useParams();
  const {
    data: patientInfo,
    dataUpdatedAt,
    isLoading,
    isPending,
  } = useGetPatient(patientId);
  const [selectedPatient, setSelectedPatient] = useSelectedPatient();
  const { isPanelExpanded } = useVisitNotePanel();

  /**
   * set initial count for communication tab from the DB
   *
   * @description using useFcmSmsCountGet to prevent after refresh the
   */
  const { refetch: refetchSmsCount } = useFcmSmsCountGet({ patientId });

  // The push payload's unread_count alone leaves the badge stale; the server count is the source of truth.
  useFcmSmsEventListener({
    onSmsNotification: () => refetchSmsCount(),
    onResetSmsCount: () => refetchSmsCount(),
  });

  /** set selected patient */
  useEffect(() => {
    // PatientProfile is a hand-written mirror of the patient record that
    // declares every field required, while the API model has them all
    // optional. Cast until that duplicate type is retired.
    if (patientInfo) setSelectedPatient(patientInfo as PatientProfile);

    return () => {
      setSelectedPatient(undefined);
    };
  }, [patientInfo, dataUpdatedAt, isPending, isLoading]);

  return (
    <Flex height={`calc(100vh - ${NAVBAR_HEIGHT})`} flexDirection="column">
      {/* <PinboardBar
        patientName={
          patientInfo?.firstName || patientInfo?.lastName
            ? `${patientInfo?.firstName ?? ""} ${patientInfo?.lastName ?? ""}`.trim()
            : "Patient"
        }
        onClose={() => navigate(PATIENT_LIST_PATH)}
      /> */}
      <Flex flex={1} minHeight={0}>
        {/* side bar */}
        <Box
          width={"357px"}
          // minWidth={{ xs: "280px", lg: "356px", xl: "357px" }}
          // maxWidth={{ xs: "280px", lg: "356px", xl: "357px" }}
          height={"inherit"}
          sx={{
            bgcolor: CHART_COLORS.surfaceDefault,
            borderRight: `1px solid ${CHART_COLORS.border}`,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <PatientSideBar data={selectedPatient} />
        </Box>

        {/* header sec */}
        <Flex
          flex={1}
          boxSizing={"border-box"}
          flexDirection={"column"}
          minWidth={0}
          overflow={"hidden"}
          sx={{ position: "relative" }}
        >
          {/* Background dim overlay when visit note panel is expanded */}
          {isPanelExpanded && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                zIndex: 3,
                transition: "background-color 0.3s ease-in-out",
                borderRadius: "4px",
              }}
            />
          )}
          <Box
            width={"100%"}
            flexShrink={0}
            sx={{
              position: "relative",
              filter: isPanelExpanded ? "blur(0.3px)" : "none",
              opacity: isPanelExpanded ? 0.6 : 1,
              pointerEvents: isPanelExpanded ? "none" : "auto",
              transition: "filter 0.3s ease-in-out, opacity 0.3s ease-in-out",
              bgcolor: CHART_COLORS.surfaceWhite,
              borderBottom: `1px solid ${CHART_COLORS.border}`,
              boxSizing: "border-box",
            }}
          >
            <Flex
              width={"100%"}
              overflow={"auto"}
              sx={{
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              <PatientChartTabs />
            </Flex>
          </Box>

          {/* other active tab outlet sec */}

          <Flex flex={1} width={"100%"} sx={{ overflow: "hidden" }}>
            <Outlet context={{ patientData: patientInfo }} />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

const PatientChartNewLayout = () => (
  <VisitNotePanelProvider>
    <PatientChartNewLayoutInner />
  </VisitNotePanelProvider>
);

export default PatientChartNewLayout;
